import type { z } from 'zod';
import { type Subject } from '../common/enums';
import { type TransformerConfig } from '../common/types';
import { createDecodeFromDbFn, createEncodeToDbFn, createTransformer } from '../common/utils';
import {
  DBLegacyExamQuestionAnalyticsSchema,
  DBLegacyExamQuestionSchema,
  LegacyExamQuestionSchema,
} from './schemas';

type DBLegacyExamQuestion = z.infer<typeof DBLegacyExamQuestionSchema>;
type DBLegacyExamQuestionAnalytics = z.infer<typeof DBLegacyExamQuestionAnalyticsSchema>;
type LegacyExamQuestion = z.infer<typeof LegacyExamQuestionSchema>;

export const LegacyExamQuestionCodecConfig: TransformerConfig<
  typeof LegacyExamQuestionSchema,
  typeof DBLegacyExamQuestionSchema
> = {
  name: 'LegacyExamQuestion',
  clientSchema: LegacyExamQuestionSchema,
  dbSchema: DBLegacyExamQuestionSchema,
  transformers: [
    // examSubject + examId ↔ subjectExamIdentifier: 'subject#examId'
    createTransformer<typeof LegacyExamQuestionSchema, typeof DBLegacyExamQuestionSchema>({
      encoder: {
        sourceFields: ['examSubject', 'examId'] as const,
        encode: ({ examSubject, examId }) => ({
          subjectExamIdentifier: `${examSubject as LegacyExamQuestion['examSubject']}#${examId as LegacyExamQuestion['examId']}`,
        }),
      },
      decoder: {
        sourceFields: ['subjectExamIdentifier'] as const,
        decode: ({ subjectExamIdentifier }) => {
          const parts = (
            subjectExamIdentifier as DBLegacyExamQuestion['subjectExamIdentifier']
          ).split('#');
          return {
            examSubject: parts[0] as Subject,
            examId: parts[1] as string,
          };
        },
      },
    }),
    // active: boolean ↔ active: 'true' | 'false'
    createTransformer<typeof LegacyExamQuestionSchema, typeof DBLegacyExamQuestionSchema>({
      encoder: {
        sourceFields: ['active'] as const,
        encode: ({ active }) => ({
          active: (active as LegacyExamQuestion['active']) ? 'true' : 'false',
        }),
      },
      decoder: {
        sourceFields: ['active'] as const,
        decode: ({ active }) => ({
          active: (active as DBLegacyExamQuestion['active']) === 'true',
        }),
      },
    }),
    // analytics: compute average on decode, strip average on encode
    createTransformer<typeof LegacyExamQuestionSchema, typeof DBLegacyExamQuestionSchema>({
      encoder: {
        sourceFields: ['analytics'] as const,
        encode: ({ analytics }) => {
          const { average: _, ...dbAnalytics } = analytics as LegacyExamQuestion['analytics'];
          return { analytics: dbAnalytics };
        },
      },
      decoder: {
        sourceFields: ['analytics'] as const,
        decode: ({ analytics }) => {
          const dbAnalytics = analytics as DBLegacyExamQuestionAnalytics;
          return {
            analytics: {
              ...dbAnalytics,
              average:
                dbAnalytics.uniqueAttempts > 0
                  ? dbAnalytics.aggregateMark / dbAnalytics.uniqueAttempts
                  : 0,
            },
          };
        },
      },
    }),
    // topics: string[] ↔ topics: Record<string, {}>
    createTransformer<typeof LegacyExamQuestionSchema, typeof DBLegacyExamQuestionSchema>({
      encoder: {
        sourceFields: ['topics'] as const,
        encode: ({ topics }) => ({
          topics: Object.fromEntries(
            (topics as LegacyExamQuestion['topics']).map(topicId => [topicId, {}])
          ),
        }),
      },
      decoder: {
        sourceFields: ['topics'] as const,
        decode: ({ topics }) => ({
          topics: Object.keys(topics as DBLegacyExamQuestion['topics']),
        }),
      },
    }),
  ],
};

export const encodeLegacyExamQuestionToDb = createEncodeToDbFn(LegacyExamQuestionCodecConfig);
export const decodeLegacyExamQuestionFromDb = createDecodeFromDbFn(LegacyExamQuestionCodecConfig);
